'use client'

import { useEffect, useRef, useState } from 'react'

import { AppLink, useAppSearchParams } from '@/shared/lib/router'
import { useSession } from 'next-auth/react'

import { useMutation, useQueryClient } from '@tanstack/react-query'

import type { UserGroup } from '@/features/groups/types/groups'
import { cn } from '@/shared/lib/utils'
import { api } from '@/shared/services/api'
import { Button } from '@/shared/ui/Button'
import { Card, CardBody, CardHeader, CardTitle } from '@/shared/ui/Card'
import { inputClassName } from '@/shared/ui/input-styles'
import { Page } from '@/shared/ui/Page'


export function JoinGroupPage() {
  const searchParams = useAppSearchParams()
  const codeFromUrl = searchParams.get('code')
  const { status } = useSession()
  const queryClient = useQueryClient()
  const [codeInput, setCodeInput] = useState(codeFromUrl ?? '')
  const [message, setMessage] = useState<{
    type: 'success' | 'error'
    text: string
  } | null>(null)
  const hasTriedUrlJoin = useRef(false)

  const joinMutation = useMutation({
    mutationFn: async (code: string) => {
      const { data } = await api.post<{ record?: UserGroup }>(
        '/user/groups/join',
        {
          code: code.trim()
        }
      )
      return data
    },
    onSuccess: () => {
      setMessage({ type: 'success', text: 'Você entrou no grupo!' })
      queryClient.invalidateQueries({ queryKey: ['/user/groups'] })
      setCodeInput('')
    },
    onError: (err: {
      response?: { data?: { message?: string; errors?: string[] } }
    }) => {
      const msg =
        err?.response?.data?.message ??
        err?.response?.data?.errors?.[0] ??
        'Não foi possível entrar no grupo. Verifique o código ou se o grupo está aberto.'
      setMessage({ type: 'error', text: msg })
    }
  })

  useEffect(() => {
    if (
      status === 'authenticated' &&
      codeFromUrl?.trim() &&
      !hasTriedUrlJoin.current
    ) {
      hasTriedUrlJoin.current = true
      joinMutation.mutate(codeFromUrl.trim())
    }
  }, [status, codeFromUrl])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setMessage(null)
    if (!codeInput.trim()) {
      setMessage({ type: 'error', text: 'Informe o código do grupo.' })
      return
    }
    joinMutation.mutate(codeInput.trim())
  }

  if (status === 'unauthenticated' || status === 'loading') {
    return (
      <Page variant="default">
        <Card variant="default" className="max-w-md mx-auto">
          <CardBody>
            <p className="rounded-2xl border border-dashed border-[#E0D7FF] bg-white/80 px-4 py-6 text-center text-sm text-[#6B6883]">
              Faça login para entrar em um grupo.
            </p>
          </CardBody>
        </Card>
      </Page>
    )
  }

  return (
    <Page variant="default">
      <Card variant="default" className="max-w-md mx-auto">
        <CardHeader>
          <CardTitle>Entrar em um grupo</CardTitle>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <label className="text-sm font-medium">
              Código do grupo
              <input
                type="text"
                value={codeInput}
                onChange={e => setCodeInput(e.target.value)}
                placeholder="Cole o código ou slug do grupo"
                className={cn('mt-1', inputClassName)}
              />
            </label>
            {message && (
              <p
                className={cn(
                  'text-sm font-medium',
                  message.type === 'success'
                    ? 'text-success'
                    : 'text-destructive'
                )}
              >
                {message.text}
              </p>
            )}
            <Button type="submit" disabled={joinMutation.isPending}>
              {joinMutation.isPending ? 'Entrando...' : 'Entrar no grupo'}
            </Button>
          </form>
          <p className="mt-3 text-sm text-[#6B6883] text-center">
            <AppLink
              href="/grupos"
              className="font-medium text-jacaranda hover:underline"
            >
              Ver meus grupos
            </AppLink>
          </p>
        </CardBody>
      </Card>
    </Page>
  )
}
